import React, { useEffect, useMemo, useState } from 'react';
import { getReversalHistory } from '../api/brapi';
import './PortfolioPositionChart.css';
import { formatDateTimeBR, formatTradingDate, formatTradingDateShort } from '../utils/dates';

const CHART = {
  width: 780,
  height: 356,
  padLeft: 52,
  padRight: 72,
  padTop: 20,
  padBottom: 46,
};

const COLORS = {
  grid: '#21303d',
  muted: '#718696',
  text: '#d8e3ed',
  candleUp: '#36c79f',
  candleDown: '#ed6c68',
  ema: '#55b7e8',
  sma: '#a78bfa',
  entry: '#f2bd59',
  stop: '#ed6c68',
  target: '#49c99d',
  current: '#8fc1ff',
};

const CHART_CONTEXT_BARS = 20;
const DEFAULT_CHART_BARS = 126;

function finite(value) {
  return value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value));
}

function numberOrNull(value) {
  return finite(value) ? Number(value) : null;
}

function formatPrice(value) {
  if (!finite(value)) return '—';
  return `R$ ${Number(value).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatPnL(value) {
  if (!finite(value)) return '—';
  const number = Number(value);
  const sign = number > 0 ? '+' : number < 0 ? '−' : '';
  return `${sign}R$ ${Math.abs(number).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatPct(value, showSign = true) {
  if (!finite(value)) return '—';
  const number = Number(value);
  const sign = showSign && number > 0 ? '+' : '';
  return `${sign}${number.toFixed(2)}%`;
}

function formatDate(value) {
  return formatTradingDateShort(value);
}

function dateKey(value) {
  if (!value) return null;
  const stringValue = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(stringValue)) return stringValue.slice(0, 10);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10);
}

function chartRangeForPosition(historyBars, position) {
  if (!historyBars.length) return [];

  const entryDate = dateKey(position.entry_bar_time || position.entry_date);
  const exitDate = dateKey(position.exit_bar_time || position.exit_date);
  const entryIndex = entryDate ? historyBars.findIndex(bar => bar.date === entryDate) : -1;
  const exitIndex = exitDate ? historyBars.findIndex(bar => bar.date === exitDate) : -1;
  const availableLast = historyBars.length - 1;

  if (entryIndex < 0 && exitIndex < 0) return historyBars.slice(-DEFAULT_CHART_BARS);

  let start = entryIndex >= 0
    ? Math.max(0, entryIndex - CHART_CONTEXT_BARS)
    : Math.max(0, exitIndex - DEFAULT_CHART_BARS + CHART_CONTEXT_BARS);
  let end = exitIndex >= 0
    ? Math.min(availableLast, exitIndex + CHART_CONTEXT_BARS)
    : availableLast;

  // Keep a useful amount of surrounding price action for short operations,
  // while never dropping either event when they are far apart.
  const desiredLength = Math.min(
    historyBars.length,
    Math.max(DEFAULT_CHART_BARS, end - start + 1),
  );
  let missing = desiredLength - (end - start + 1);
  const before = Math.min(start, Math.ceil(missing * 0.6));
  start -= before;
  missing -= before;
  end = Math.min(availableLast, end + missing);
  if (end - start + 1 < desiredLength) start = Math.max(0, end - desiredLength + 1);

  return historyBars.slice(start, end + 1);
}

function historyCoverage(history, position) {
  const bars = history?.bars || [];
  if (!bars.length) return null;
  const firstDate = dateKey(history.firstDate || bars[0].date);
  const lastDate = dateKey(history.lastDate || bars[bars.length - 1].date);
  const events = [
    { label: 'entrada', date: dateKey(position.entry_bar_time || position.entry_date) },
    ...(position.status === 'closed'
      ? [{ label: 'saída', date: dateKey(position.exit_bar_time || position.exit_date) }]
      : []),
  ].filter(event => event.date);
  const unavailable = events.filter(event => !bars.some(bar => bar.date === event.date));
  if (!unavailable.length) return null;

  const unavailableText = unavailable.map(event => {
    if (event.date < firstDate || event.date > lastDate) {
      return `${event.label} de ${formatDate(event.date)} está fora da cobertura`;
    }
    return `candle de ${event.label} em ${formatDate(event.date)} não foi retornado`;
  }).join(' e ');
  return `Histórico parcial: a BRAPI retornou candles de ${formatDate(firstDate)} a ${formatDate(lastDate)}; ${unavailableText}. Nenhuma marcação foi inventada.`;
}

function priceLine({ label, value, color, y, x1, x2, width }) {
  if (!finite(value)) return null;
  const clampedY = Math.max(8, Math.min(CHART.height - 12, y));
  return (
    <g>
      <line
        x1={x1}
        x2={x2}
        y1={clampedY}
        y2={clampedY}
        stroke={color}
        strokeWidth="1.2"
        strokeDasharray="6 4"
        opacity="0.95"
      />
      <rect x={width - CHART.padRight + 3} y={clampedY - 9} width="66" height="16" rx="3" fill={color} opacity="0.96" />
      <text
        x={width - CHART.padRight + 36}
        y={clampedY + 2.5}
        textAnchor="middle"
        fill="#071016"
        fontSize="7.5"
        fontWeight="700"
        fontFamily="'JetBrains Mono', monospace"
      >
        {label} {Number(value).toFixed(2)}
      </text>
    </g>
  );
}

function outcomeFor(position) {
  if (position.status === 'closed') {
    if (!finite(position.pnl_pct)) return { label: 'Resultado realizado', tone: 'neutral' };
    if (Number(position.pnl_pct) > 0) return { label: 'Resultado realizado · Lucro', tone: 'positive' };
    if (Number(position.pnl_pct) < 0) return { label: 'Resultado realizado · Prejuízo', tone: 'negative' };
    return { label: 'Resultado realizado · No preço', tone: 'neutral' };
  }
  if (!finite(position.current_price)) return { label: 'Sem cotação', tone: 'muted' };
  if (!finite(position.pnl_pct)) return { label: 'Cotação disponível', tone: 'neutral' };
  if (Number(position.pnl_pct) > 0) return { label: 'Em lucro', tone: 'positive' };
  if (Number(position.pnl_pct) < 0) return { label: 'Em perda', tone: 'negative' };
  return { label: 'No preço', tone: 'neutral' };
}

function PositionChartSvg({ history, position }) {
  const bars = useMemo(() => chartRangeForPosition(
    (history?.bars || []).filter(bar => [bar.open, bar.high, bar.low, bar.close].every(finite)),
    position,
  ), [history, position]);

  const levels = useMemo(() => ({
    entry: numberOrNull(position.entry_price),
    stop: numberOrNull(position.stop_price),
    target: numberOrNull(position.target_price),
    current: numberOrNull(position.current_price),
  }), [position]);

  if (bars.length < 2) return null;

  const chartWidth = Math.max(
    CHART.width,
    CHART.padLeft
      + CHART.padRight
      + (bars.length - 1) * (CHART.width - CHART.padLeft - CHART.padRight) / (DEFAULT_CHART_BARS - 1),
  );
  const innerWidth = chartWidth - CHART.padLeft - CHART.padRight;
  const innerHeight = CHART.height - CHART.padTop - CHART.padBottom;
  const step = innerWidth / Math.max(1, bars.length - 1);
  const candleHalf = Math.max(1.8, Math.min(5, step * 0.32));
  const xAt = index => CHART.padLeft + (index / Math.max(1, bars.length - 1)) * innerWidth;

  const levelValues = Object.values(levels).filter(finite);
  const allHighs = bars.map(bar => Number(bar.high)).concat(levelValues);
  const allLows = bars.map(bar => Number(bar.low)).concat(levelValues);
  const rawMax = Math.max(...allHighs);
  const rawMin = Math.min(...allLows);
  const padding = (rawMax - rawMin) * 0.07 || rawMax * 0.03 || 1;
  const pMax = rawMax + padding;
  const pMin = Math.max(0, rawMin - padding);
  const pRange = pMax - pMin || 1;
  const priceY = value => CHART.padTop + ((pMax - Number(value)) / pRange) * innerHeight;
  const priceGrid = [0, 0.33, 0.66, 1].map(stepValue => pMin + (1 - stepValue) * pRange);
  const maxVolume = Math.max(...bars.map(bar => Number(bar.volume) || 0), 1);
  const entryDate = dateKey(position.entry_bar_time || position.entry_date);
  const exitDate = dateKey(position.exit_bar_time || position.exit_date);
  const entryIndex = entryDate ? bars.findIndex(bar => bar.date === entryDate) : -1;
  const exitIndex = exitDate ? bars.findIndex(bar => bar.date === exitDate) : -1;
  const currentIndex = bars.length - 1;
  const emaBars = bars.filter(bar => finite(bar.ema21));
  const smaBars = bars.filter(bar => finite(bar.sma200));

  const xLabels = bars.map((bar, index) => {
    const labelEvery = Math.max(1, Math.ceil(bars.length / 7));
    if (index !== bars.length - 1 && index % labelEvery !== 0) return null;
    const isFirst = index === 0;
    const isLast = index === bars.length - 1;
    return (
      <text
        key={`date-${bar.date}`}
        x={xAt(index)}
        y={CHART.height - 16}
        fill={isLast ? '#d7b768' : COLORS.muted}
        fontSize="8"
        textAnchor={isFirst ? 'start' : isLast ? 'end' : 'middle'}
        fontFamily="'JetBrains Mono', monospace"
      >
        {formatDate(bar.date)}
      </text>
    );
  });

  const zone = (first, second, fill, opacity, key) => {
    if (!finite(first) || !finite(second)) return null;
    const y1 = priceY(first);
    const y2 = priceY(second);
    return (
      <rect
        key={key}
        x={CHART.padLeft}
        y={Math.min(y1, y2)}
        width={innerWidth}
        height={Math.max(1, Math.abs(y2 - y1))}
        fill={fill}
        opacity={opacity}
      />
    );
  };

  return (
    <svg
      className="portfolio-position-chart-svg"
      viewBox={`0 0 ${chartWidth} ${CHART.height}`}
      style={{ width: chartWidth > CHART.width ? `${chartWidth}px` : undefined }}
      role="img"
      aria-label={`Gráfico de candles de ${position.ticker} com estratégia, entrada, stop e alvo`}
    >
      <rect x={CHART.padLeft} y={CHART.padTop} width={innerWidth} height={innerHeight} fill="rgba(255,255,255,0.012)" rx="3" />
      {priceGrid.map((value, index) => (
        <g key={`grid-${index}`}>
          <line
            x1={CHART.padLeft}
            x2={chartWidth - CHART.padRight}
            y1={priceY(value)}
            y2={priceY(value)}
            stroke={COLORS.grid}
            strokeWidth="0.8"
          />
          <text
            x={CHART.padLeft - 6}
            y={priceY(value) + 3}
            fill={COLORS.muted}
            fontSize="8"
            textAnchor="end"
            fontFamily="'JetBrains Mono', monospace"
          >
            {Number(value).toFixed(2)}
          </text>
        </g>
      ))}

      {zone(levels.entry, levels.stop, COLORS.stop, 0.08, 'risk-zone')}
      {zone(levels.entry, levels.target, COLORS.target, 0.08, 'reward-zone')}

      <text x={CHART.padLeft + 6} y={CHART.padTop + 12} fill="#91a6b6" fontSize="8" fontWeight="700" fontFamily="'JetBrains Mono', monospace">
        PREÇO · OPERAÇÃO
      </text>

      {bars.map((bar, index) => {
        const up = Number(bar.close) >= Number(bar.open);
        const color = up ? COLORS.candleUp : COLORS.candleDown;
        const x = xAt(index);
        const bodyTop = priceY(Math.max(Number(bar.open), Number(bar.close)));
        const bodyBottom = priceY(Math.min(Number(bar.open), Number(bar.close)));
        return (
          <g key={bar.date}>
            <title>{`${formatDate(bar.date)} · O ${formatPrice(bar.open)} · H ${formatPrice(bar.high)} · L ${formatPrice(bar.low)} · C ${formatPrice(bar.close)}`}</title>
            <line x1={x} x2={x} y1={priceY(bar.high)} y2={priceY(bar.low)} stroke={color} strokeWidth="0.9" />
            <rect x={x - candleHalf} y={bodyTop} width={candleHalf * 2} height={Math.max(1, bodyBottom - bodyTop)} fill={color} />
          </g>
        );
      })}

      <path
        d={emaBars.map((bar, index) => {
          const originalIndex = bars.indexOf(bar);
          return `${index === 0 ? 'M' : 'L'} ${xAt(originalIndex).toFixed(1)},${priceY(bar.ema21).toFixed(1)}`;
        }).join(' ')}
        fill="none"
        stroke={COLORS.ema}
        strokeWidth="1.2"
        opacity="0.9"
      />
      <path
        d={smaBars.map((bar, index) => {
          const originalIndex = bars.indexOf(bar);
          return `${index === 0 ? 'M' : 'L'} ${xAt(originalIndex).toFixed(1)},${priceY(bar.sma200).toFixed(1)}`;
        }).join(' ')}
        fill="none"
        stroke={COLORS.sma}
        strokeWidth="1.1"
        strokeDasharray="5 4"
        opacity="0.85"
      />

      {entryIndex >= 0 && (
        <g data-testid="portfolio-entry-marker">
          <line x1={xAt(entryIndex)} x2={xAt(entryIndex)} y1={CHART.padTop} y2={CHART.height - CHART.padBottom} stroke={COLORS.entry} strokeDasharray="2 4" opacity="0.45" />
          <circle cx={xAt(entryIndex)} cy={priceY(levels.entry)} r="4" fill={COLORS.entry} stroke="#101923" strokeWidth="1.5" />
          <text x={xAt(entryIndex)} y={CHART.padTop + 24} textAnchor="middle" fill={COLORS.entry} fontSize="7.5" fontWeight="700" fontFamily="'JetBrains Mono', monospace">ENTRADA</text>
        </g>
      )}
      {exitIndex >= 0 && (
        <g data-testid="portfolio-exit-marker">
          <line x1={xAt(exitIndex)} x2={xAt(exitIndex)} y1={CHART.padTop} y2={CHART.height - CHART.padBottom} stroke={COLORS.current} strokeDasharray="2 4" opacity="0.5" />
          <circle cx={xAt(exitIndex)} cy={priceY(levels.current)} r="4" fill={COLORS.current} stroke="#101923" strokeWidth="1.5" />
          <text x={xAt(exitIndex)} y={CHART.padTop + 36} textAnchor="middle" fill={COLORS.current} fontSize="7.5" fontWeight="700" fontFamily="'JetBrains Mono', monospace">SAÍDA</text>
        </g>
      )}

      {finite(levels.entry) && priceLine({ label: 'ENTRADA', value: levels.entry, color: COLORS.entry, y: priceY(levels.entry), x1: CHART.padLeft, x2: chartWidth - CHART.padRight, width: chartWidth })}
      {finite(levels.stop) && priceLine({ label: 'STOP', value: levels.stop, color: COLORS.stop, y: priceY(levels.stop), x1: CHART.padLeft, x2: chartWidth - CHART.padRight, width: chartWidth })}
      {finite(levels.target) && priceLine({ label: 'ALVO', value: levels.target, color: COLORS.target, y: priceY(levels.target), x1: CHART.padLeft, x2: chartWidth - CHART.padRight, width: chartWidth })}
      {finite(levels.current) && priceLine({
        label: position.status === 'closed' ? 'SAÍDA' : 'ATUAL',
        value: levels.current,
        color: COLORS.current,
        y: priceY(levels.current),
        x1: xAt(currentIndex),
        x2: chartWidth - CHART.padRight,
        width: chartWidth,
      })}

      <line x1={CHART.padLeft} x2={chartWidth - CHART.padRight} y1={CHART.height - CHART.padBottom + 2} y2={CHART.height - CHART.padBottom + 2} stroke={COLORS.grid} />
      {bars.map((bar, index) => {
        const volume = Number(bar.volume) || 0;
        const volumeHeight = (volume / maxVolume) * 26;
        const color = Number(bar.close) >= Number(bar.open) ? COLORS.candleUp : COLORS.candleDown;
        return (
          <rect
            key={`volume-${bar.date}`}
            x={xAt(index) - candleHalf}
            y={CHART.height - CHART.padBottom + 28 - volumeHeight}
            width={candleHalf * 2}
            height={Math.max(1, volumeHeight)}
            fill={color}
            opacity="0.45"
          />
        );
      })}
      <text x={CHART.padLeft + 6} y={CHART.height - CHART.padBottom + 13} fill={COLORS.muted} fontSize="7.5" fontWeight="700" fontFamily="'JetBrains Mono', monospace">
        VOLUME
      </text>
      {xLabels}
    </svg>
  );
}

export default function PortfolioPositionChart({ position }) {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [insufficient, setInsufficient] = useState(false);
  const requestedRange = useMemo(() => {
    const dates = [position?.entry_bar_time || position?.entry_date, position?.exit_bar_time || position?.exit_date]
      .map(dateKey)
      .filter(Boolean);
    if (!dates.length) return '2y';
    const oldest = new Date(`${dates.sort()[0]}T12:00:00`);
    const ageDays = (Date.now() - oldest.getTime()) / 86400000;
    return ageDays > 370 ? 'max' : '2y';
  }, [position?.entry_bar_time, position?.entry_date, position?.exit_bar_time, position?.exit_date]);

  useEffect(() => {
    let active = true;
    setHistory(null);
    setError(null);
    setInsufficient(false);
    if (!position?.ticker) {
      setLoading(false);
      return () => { active = false; };
    }

    setLoading(true);
    getReversalHistory(position.ticker, { range: requestedRange })
      .then(result => {
        if (!active) return;
        if (!result?.bars || result.bars.length < 2) {
          setInsufficient(true);
          return;
        }
        setHistory(result);
      })
      .catch(err => {
        if (!active) return;
        if (err?.response?.status === 422) {
          setInsufficient(true);
        } else {
          setError(err?.response?.data?.error || err.message || 'Não foi possível carregar o histórico do ativo.');
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [position?.ticker, requestedRange]);

  const outcome = outcomeFor(position);
  const strategy = position.strategy || 'Manual';
  const closed = position.status === 'closed';
  const referencePrice = numberOrNull(position.current_price);
  const stopDistance = referencePrice && finite(position.stop_price)
    ? ((Number(position.stop_price) - referencePrice) / referencePrice) * 100
    : null;
  const targetDistance = referencePrice && finite(position.target_price)
    ? ((Number(position.target_price) - referencePrice) / referencePrice) * 100
    : null;
  const stopHit = !closed && (
    position.stop_hit === true
    || position.stopHit === true
    || (referencePrice != null && finite(position.stop_price) && referencePrice < Number(position.stop_price))
  );
  const coverageMessage = historyCoverage(history, position);

  return (
    <section className="portfolio-position-chart" data-testid="portfolio-position-chart">
      <div className="portfolio-position-chart-head">
        <div className="portfolio-position-chart-title">
          <div className="portfolio-position-chart-ticker">
            <strong>{position.ticker}</strong>
            <span>{position.name || position.ticker}</span>
          </div>
          <div className="portfolio-position-chart-subtitle">Estratégia montada · {strategy}</div>
        </div>
        <span className={`portfolio-position-status ${stopHit ? 'stop-hit' : outcome.tone}`}>
          {stopHit ? 'STOP ATINGIDO' : outcome.label}
        </span>
      </div>

      <div className="portfolio-position-summary">
        <div>
          <span>Entrada</span>
          <strong>{formatPrice(position.entry_price)}</strong>
        </div>
        <div>
          <span>{closed ? 'Saída' : 'Cotação atual'}</span>
          <strong>{formatPrice(position.current_price)}</strong>
        </div>
        <div className={outcome.tone === 'negative' ? 'negative' : outcome.tone === 'positive' ? 'positive' : ''}>
          <span>{closed ? 'P&amp;L realizado' : 'P&amp;L atual'}</span>
          <strong>{formatPnL(position.pnl_r)}</strong>
          <small>{formatPct(position.pnl_pct)}</small>
        </div>
        <div>
          <span>Distâncias</span>
          <strong className="portfolio-position-distances">
            <em className="stop">Stop {formatPct(stopDistance)}</em>
            <em className="target">Alvo {formatPct(targetDistance)}</em>
          </strong>
        </div>
      </div>

      {(position.entry_date || position.entry_bar_time) && (
        <div className="portfolio-position-chart-meta">
          Entrada em {position.entry_bar_time ? formatDateTimeBR(position.entry_bar_time) : formatTradingDate(position.entry_date)}
          {closed && (position.exit_date || position.exit_bar_time) && ` · Saída em ${position.exit_bar_time ? formatDateTimeBR(position.exit_bar_time) : formatTradingDate(position.exit_date)}`}
          {!closed && !finite(position.stop_price) && !finite(position.target_price) && ' · Cadastre stop e alvo para acompanhar o plano'}
        </div>
      )}

      {loading && (
        <div className="portfolio-position-chart-state" data-testid="portfolio-position-chart-loading">
          <span className="portfolio-position-loader" />Carregando histórico técnico pela BRAPI…
        </div>
      )}
      {!loading && insufficient && (
        <div className="portfolio-position-chart-state" data-testid="portfolio-position-chart-insufficient">
          <strong>Histórico insuficiente</strong>
          <span>A BRAPI não retornou dados suficientes para desenhar o gráfico deste ativo.</span>
        </div>
      )}
      {!loading && error && (
        <div className="portfolio-position-chart-state error" data-testid="portfolio-position-chart-error">
          <strong>Histórico indisponível</strong>
          <span>{error}</span>
        </div>
      )}
      {!loading && !error && !insufficient && history && (
        <>
          {coverageMessage && (
            <div className="portfolio-position-chart-coverage" role="status" data-testid="portfolio-position-chart-coverage">
              {coverageMessage}
            </div>
          )}
          <div className="portfolio-position-chart-scroll">
            <PositionChartSvg history={history} position={position} />
          </div>
        </>
      )}

      <div className="portfolio-position-chart-legend" aria-label="Legenda do gráfico da operação">
        <span><i className="entry" /> Entrada</span>
        {finite(position.stop_price) && <span><i className="stop" /> Stop · risco</span>}
        {finite(position.target_price) && <span><i className="target" /> Alvo · retorno</span>}
        <span><i className="current" /> {closed ? 'Saída' : 'Atual'}</span>
        <span><i className="ema" /> MME21</span>
        <span><i className="sma" /> SMA200</span>
      </div>
    </section>
  );
}